import React from 'react';

import Context     from '../../../../../../context';
import validations from '../../../../../../validations';

import { useFormik         } from 'formik';
import { useTranslation    } from 'react-i18next';
import { locationNamespace } from '../../../../../../types/location.namespace';

import {
  CustomIcon,
  CustomInput,
  CustomModal,
  CustomButton2,
  CustomLeaflet,
  ResponsiveView,
  LocationDropdowns,
} from '../../../../../../components';

import './index.sass';
import CustomCheckbox from "../../../../../../components/CustomCheckbox";

const Create: React.FC = (): React.ReactElement => {

  // # =================================================================================================================
  // # --------------------------------------------- Start Validate Values ---------------------------------------------

  const [location, setLocation] = React.useState<Array<string>>([]);
  const [selectedProvince, setSelectedProvince] = React.useState<locationNamespace.province.index>();
  const [errors, setErrors] = React.useState<any>(undefined);
  const { showModal, setShowModal, createFunction, isLoading } = React.useContext(Context.Location.City);
  const { t } = useTranslation();

  // # ---------------------------------------------- End Validate Values ----------------------------------------------
  // # =================================================================================================================

  // * =================================================================================================================
  // * -------------------------------------------- Start Local Functions ----------------------------------------------

  const formik = useFormik<locationNamespace.city.create>({
    enableReinitialize: true,
    initialValues: {
      isCapital: false,

      key        : undefined,
      name       : '',
      prefix     : undefined,
      description: undefined,

      province : undefined,

      code      : undefined,
      population: undefined,

      location: {
        type: 'Point',
        coordinates: [NaN, NaN],
      }
    },
    validationSchema: validations.location.province.create,
    onSubmit: (values: locationNamespace.city.create, { setSubmitting }) => {
      if (!selectedProvince) return setErrors({province: 'اتنخاب استان اجباری است.'});
      const newValues: locationNamespace.city.create = {
        ...values,
        province : selectedProvince?._id,
        location: {
          type: 'Point',
          coordinates: [
            location[0] ? Number(location[0]) : Number(selectedProvince?.location?.coordinates[0]),
            location[1] ? Number(location[1]) : Number(selectedProvince?.location?.coordinates[1]),
          ]
        }
      }
      Promise.resolve(createFunction!(newValues)).catch(() => setSubmitting(false));
    },
  });

  async function submitHandler(): Promise<void> {
    await formik.submitForm();
  }

  function handleChangeLocation(lat: any, lng: any): void {
    setLocation([lat, lng]);
  }

  function onSelectProvince(province: locationNamespace.province.index): void {
    setSelectedProvince(province);
    setErrors({province: undefined});
  }

  // * --------------------------------------------- End Local Functions -----------------------------------------------
  // * =================================================================================================================

  // ? =================================================================================================================
  // ? ----------------------------------------------- Start Return View -----------------------------------------------

  return (
    <section id="parent-create-modal-container">
      <CustomIcon
        icon="fa-sharp fa-solid fa-circle-plus"
        size="medium"
        hoverTitle={'ساخت شهر'}
        onClick={() => {setShowModal!("CREATE")}}
      />
      <form onSubmit={submitHandler}>
        <CustomModal label='ساخت شهر' open={showModal === "CREATE"} onClose={() => setShowModal!("NONE")}>
          <ResponsiveView.Container className="create-form-content">
            <br/>
            <hr/>
            <ResponsiveView.Row className="form-row">
              <ResponsiveView.Column medium={6}>
                <LocationDropdowns.Province
                  onSelectItem = { onSelectProvince }
                  defaultItem  = { selectedProvince }
                  errorText    = { errors?.province }
                  isError      = { errors?.province }
                />
              </ResponsiveView.Column>
            </ResponsiveView.Row>
            <hr/>
            <ResponsiveView.Row className="form-row">
              <ResponsiveView.Column medium={6}>
                <CustomInput
                  id            = 'name'
                  name          = 'name'
                  label         = 'نام شهر'
                  value         = { formik.values.name }
                  error         = { formik.errors.name && formik.touched.name }
                  errorText     = { formik.errors.name }
                  formikProps   = { {onBlur: formik.handleBlur} }
                  onValueChange = { formik.handleChange }
                />
              </ResponsiveView.Column>
              <ResponsiveView.Column medium={6}>
                <CustomCheckbox label='مرگز استان میباشد'/>
              </ResponsiveView.Column>
            </ResponsiveView.Row>
            <br/>
            <ResponsiveView.Row className='form-row'>
              <ResponsiveView.Column small={12}>
                <CustomLeaflet
                  onChangeLatAndLng={handleChangeLocation}
                  defaultLat={location[0]}
                  defaultLng={location[1]}
                  defaultLatArea={selectedProvince?.location?.coordinates[0]}
                  defaultLngArea={selectedProvince?.location?.coordinates[1]}
                />
              </ResponsiveView.Column>
            </ResponsiveView.Row>
            <br />
            <ResponsiveView.Row className="form-row">
              <ResponsiveView.Column medium={6}>
                <CustomInput
                  id           = 'key'
                  key          = 'key'
                  label        = 'شناسه شهر'
                  value        = { formik.values.key }
                  error        = { formik.errors.key && formik.touched.key }
                  errorText    = { formik.errors.key }
                  formikProps  = { {onBlur: formik.handleBlur} }
                  onValueChange= { formik.handleChange }
                />
              </ResponsiveView.Column>
              <ResponsiveView.Column medium={6}>
                <CustomInput
                  id           = 'prefix'
                  key          = 'prefix'
                  label        = 'پیش شماره‌ی شهر'
                  value        = { formik.values.prefix }
                  error        = { formik.errors.prefix && formik.touched.prefix }
                  errorText    = { formik.errors.prefix }
                  formikProps  = { {onBlur: formik.handleBlur} }
                  onValueChange= { formik.handleChange }
                />
              </ResponsiveView.Column>
            </ResponsiveView.Row>
            <hr/>
            <ResponsiveView.Row className="form-row">
              <ResponsiveView.Column large={6} medium={6} small={12}>
                <CustomButton2
                  isLoading={isLoading}
                  onClick={() => setShowModal!("NONE")}
                  title={t("cancel")}
                  type="danger"
                />
              </ResponsiveView.Column>
              <ResponsiveView.Column large={6} medium={6} small={12}>
                <CustomButton2 isLoading={isLoading} onClick={submitHandler} title={t("submit")}/>
              </ResponsiveView.Column>
            </ResponsiveView.Row>
          </ResponsiveView.Container>
        </CustomModal>
      </form>
    </section>
  );

  // ? ------------------------------------------------ End Return View ------------------------------------------------
  // ? =================================================================================================================

};

export default Create;
